import React, { useState, useEffect, useImperativeHandle, useMemo, useCallback, ReactNode, useRef, forwardRef, useContext } from "react";
import classnames from 'classnames';
import './TimePeriodStep.scss';
import { ByzzerButton, StepRef, WizardActions, WizardContent, WizardHeader, WizardStep } from "@byzzer/ui-components";
import TimePeriodFilters, { TimePeriodFiltersProps } from "./TimePeriodFilters";
import { DefaultConfigWizardContext } from "../../DefaultsConfigWizard/DefaultsConfigWizardContext";
import { MonthEndingTimePeriod, TimePeriod, WeekEndingTimePeriod } from "@/types/ReportRun";

export type TimePeriodStepProps = {
    data?: any;
    className?: string;
    title?: ReactNode;
    onComplete?: () => void;
    onCancel?: () => void;
    enabled?: boolean;
    disableSave?: boolean;
    saveDisabledTip?: string;
} & TimePeriodFiltersProps

export const TimePeriodStep = forwardRef<StepRef, TimePeriodStepProps>((
    {
        className,
        title,
        onCancel,
        onComplete,
        enabled,
        disableSave,
        saveDisabledTip,
        ...props
    },
    ref
) => {

    const baseClassName = 'byz-time-period-step';
    const { value: contextValue } = useContext(DefaultConfigWizardContext);
    const timePeriod = contextValue?.timePeriod as TimePeriod | undefined;

    const isTimePeriodValid = useMemo(() => {
        if (!timePeriod) {
            return false;
        }

        if (['osh', 'omni', 'cps_relative', 'relative'].includes(timePeriod.type)) {
            return true;
        }

        if (['week_ending', 'cps_custom', 'omni_custom'].includes(timePeriod.type)) {
            if (timePeriod.type === 'week_ending') {
                const tp = timePeriod as WeekEndingTimePeriod;
                return Boolean(tp.period && tp.period.weeks! > 0 && tp.period.endDate);
            }

            if (timePeriod.type === 'omni_custom' || timePeriod.type === 'cps_custom') {
                const tp = timePeriod as MonthEndingTimePeriod;
                return Boolean(tp.period && tp.period.months! > 0 && tp.period.endDate);
            }
        }

        return false;
    }, [timePeriod]);

    function handleCancelClick() {
        onCancel?.();
    }

    function handleComplete(): boolean {
        onComplete?.();
        return false;
    }

    return (
        <WizardStep 
            className={classnames(baseClassName, className)} 
            ref={ref}
            title={title}
            enabled
        >
            <WizardHeader>
                <h1 className={'report-run-config-wizard__step-title'}>{title}</h1>
            </WizardHeader>
            <WizardContent>
                <TimePeriodFilters 
                    {...props}
                />
            </WizardContent>
            <WizardActions 
                nextText="Save"
                beforeNext={handleComplete}
                disableNext={Boolean(disableSave || !isTimePeriodValid)}
                nextDisabledTip={saveDisabledTip}
            >
                <ByzzerButton onClick={handleCancelClick} type={'negative'}>Cancel</ByzzerButton>
            </WizardActions>
        </WizardStep>
    )

});

export default TimePeriodStep;

TimePeriodStep.displayName = 'TimePeriodStep';